// MARK://dhk8/admin-shell/pages/modules/OrganizationsPage
// Lista de organizaciones (entidad padre que agrupa N branches) →
// dhk8-platform-tenants. Visible solo para usuarios con rol superadmin
// (App.tsx aplica el RequireRole sobre la ruta).
//
// V1 — no hay GET /v1/organizations todavía; renderizamos:
//   - Las orgs visitadas recientemente (cacheadas en localStorage).
//   - Una caja "Abrir por ID" para pegar un orgId y navegar al detalle.
//   - CTA "Crear organización" que abre un Sheet con displayName + ownerUserId
//     (+ branches opcional). Por ahora el backend de creación está pendiente —
//     el sheet muestra el error real del cliente.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Network, ChevronRight, X } from 'lucide-react'
import * as orgs from '../../lib/clients/platform-orgs.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import Sheet from '../../components/Sheet.js'

interface CreateForm { displayName: string; ownerUserId: string; branches: string }
const emptyForm = (): CreateForm => ({ displayName: '', ownerUserId: '', branches: '' })

export default function OrganizationsPage() {
  const navigate = useNavigate()
  const list = useAsync((s) => orgs.listOrganizations(s))

  const [openById, setOpenById] = useState('')
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<CreateForm>(emptyForm())
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  function openCreate() { setForm(emptyForm()); setFormError(null); setCreating(true) }

  async function saveCreate() {
    setFormError(null)
    const displayName = form.displayName.trim()
    const ownerUserId = form.ownerUserId.trim()
    if (!displayName) { setFormError('El nombre es obligatorio.'); return }
    if (!ownerUserId) { setFormError('El owner (userId) es obligatorio.'); return }
    const branches = form.branches.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean).map((liveTenantId) => ({ liveTenantId }))
    setSaving(true)
    try {
      const created = await orgs.createOrganization({ displayName, ownerUserId, branches })
      setCreating(false)
      list.reload()
      navigate(`/organizations/${created.id}`)
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo crear la organización.')
    } finally { setSaving(false) }
  }

  function navigateById() {
    const id = openById.trim()
    if (!id) return
    navigate(`/organizations/${id}`)
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Organizaciones</h1>
          <p className="adm-page__sub">Entidad padre que agrupa varias sucursales (branches) y consolida ventas</p>
        </div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={openCreate}>
            <Plus size={16} /> Crear organización
          </button>
        </div>
      </div>

      <div className="adm-card adm-card--pad" style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <label className="adm-field" style={{ flex: '1 1 280px' }}>
          <span className="adm-field__label">Abrir organización por ID</span>
          <input
            className="adm-input"
            value={openById}
            onChange={(e) => setOpenById(e.target.value)}
            placeholder="UUID de la organización"
            onKeyDown={(e) => { if (e.key === 'Enter') navigateById() }}
          />
        </label>
        <button className="adm-btn" onClick={navigateById} disabled={!openById.trim()}>
          Abrir
        </button>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <div className="adm-card adm-card--pad0">
          <div className="adm-list">
            {list.data.length === 0 && (
              <div className="adm-empty">
                Aún no has visitado ninguna organización. Pega un UUID arriba o crea una.
              </div>
            )}
            {list.data.map((o) => (
              <div key={o.id} className="adm-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/organizations/${o.id}`)}>
                <div className="adm-row__main">
                  <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Network size={14} aria-hidden style={{ opacity: 0.6 }} />
                    {o.displayName}
                  </div>
                  <div className="adm-row__meta">
                    {o.slug ? `${o.slug} · ` : ''}
                    {o.branchCount != null ? `${o.branchCount} sucursal${o.branchCount === 1 ? '' : 'es'}` : ''}
                    {' · '}{o.id}
                  </div>
                </div>
                <div className="adm-row__tail" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="adm-btn adm-btn--ghost adm-btn--sm"
                    onClick={() => { orgs.forgetOrg(o.id); list.reload() }}
                    aria-label="Quitar de recientes"
                    title="Quitar de recientes"
                  >
                    <X size={14} />
                  </button>
                  <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate(`/organizations/${o.id}`)} aria-label="Ver detalle">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Sheet
        open={creating}
        title="Crear organización"
        onClose={() => { setCreating(false); setFormError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveCreate} disabled={saving || !form.displayName.trim() || !form.ownerUserId.trim()}>
            {saving ? 'Guardando…' : 'Crear'}
          </button>
        </>}
      >
        {formError && <div className="adm-banner adm-banner--danger" role="alert">{formError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre comercial</span>
          <input
            className="adm-input"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            placeholder="Ej: SAVIS COFFEE"
          />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Owner (userId)</span>
          <input
            className="adm-input"
            value={form.ownerUserId}
            onChange={(e) => setForm({ ...form, ownerUserId: e.target.value })}
            placeholder="UUID del usuario dueño"
          />
        </label>
        <label className="adm-field">
          <span className="adm-field__label">Branches (live tenant IDs, opcional)</span>
          <input
            className="adm-input"
            value={form.branches}
            onChange={(e) => setForm({ ...form, branches: e.target.value })}
            placeholder="UUIDs separados por coma o espacio"
          />
        </label>
        <p className="adm-row__meta" style={{ marginTop: 8 }}>
          El backend de creación (POST /v1/organizations) está pendiente. Por ahora,
          las orgs se aprovisionan vía script y este formulario se conecta cuando esté lista la ruta.
        </p>
      </Sheet>
    </div>
  )
}
